import React, { useState, useRef } from 'react';
import { Button, Icon, Input, Loader, Message } from 'semantic-ui-react';
import GlassmorphismTheme from '../../../styles/glassmorphism';
import { ConversationService } from '../services/ConversationService';

interface InputBoxProps {
  clienteId: string;
  onMessageSent: () => void;
}

const InputBox: React.FC<InputBoxProps> = ({ clienteId, onMessageSent }) => {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);

  // ===== AUTO RESIZE TEXTAREA =====
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  };

  // ===== SEND MESSAGE =====
  const handleSendMessage = async () => {
    if (!message.trim()) return;

    setLoading(true);
    setError(null);
    try {
      await ConversationService.sendMessage(clienteId, {
        clienteId,
        conteudo: message,
        tipo: 'texto',
      });
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      onMessageSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao enviar mensagem');
    } finally {
      setLoading(false);
    }
  };

  // ===== AUDIO RECORDING =====
  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunks.current = [];

      recorder.ondataavailable = (e) => {
        audioChunks.current.push(e.data);
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/mpeg' });
        const file = new File([audioBlob], 'audio.mp3', { type: 'audio/mpeg' });
        
        try {
          const response = await ConversationService.uploadAudio(clienteId, file);
          await ConversationService.sendMessage(clienteId, {
            clienteId,
            conteudo: 'Áudio enviado',
            tipo: 'audio',
          });
          onMessageSent();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Erro ao enviar áudio');
        }
      };

      mediaRecorder.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      setError('Permissão para usar microfone negada');
    }
  };

  // ===== STOP RECORDING =====
  const handleStopRecording = () => {
    if (mediaRecorder.current) {
      mediaRecorder.current.stop();
      mediaRecorder.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  // ===== FILE UPLOAD =====
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setFileName(file.name);

    try {
      const response = await ConversationService.uploadAttachment(clienteId, file);
      await ConversationService.sendMessage(clienteId, {
        clienteId,
        conteudo: `Anexo: ${file.name}`,
        tipo: 'anexo',
      });
      setFileName('');
      onMessageSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao enviar anexo');
    } finally {
      setLoading(false);
    }
  };

  // ===== HANDLE ENTER KEY =====
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.ctrlKey && e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <div>
      {error && <Message error content={error} onDismiss={() => setError(null)} />}

      <div style={{
        display: 'flex',
        gap: GlassmorphismTheme.spacing.sm,
        marginBottom: GlassmorphismTheme.spacing.sm,
      }}>
        {/* AUDIO BUTTON */}
        <Button
          icon
          onClick={isRecording ? handleStopRecording : handleStartRecording}
          loading={isRecording}
          color={isRecording ? 'red' : undefined}
          style={{
            ...GlassmorphismTheme.components.buttonGlass,
            minWidth: '40px',
            padding: '0',
          }}
          title={isRecording ? 'Parar gravação' : 'Gravar áudio'}
        >
          <Icon name={isRecording ? 'stop' : 'microphone'} />
        </Button>

        {/* FILE UPLOAD BUTTON */}
        <Button
          icon
          onClick={() => fileInputRef.current?.click()}
          style={{
            ...GlassmorphismTheme.components.buttonGlass,
            minWidth: '40px',
            padding: '0',
          }}
          title="Enviar anexo"
        >
          <Icon name="paperclip" />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        {/* SEND BUTTON */}
        <Button
          icon
          onClick={handleSendMessage}
          loading={loading}
          disabled={!message.trim() || loading}
          style={{
            ...GlassmorphismTheme.components.buttonPrimary,
            minWidth: '40px',
            padding: '0',
          }}
          title="Enviar (Ctrl+Enter)"
        >
          <Icon name="send" />
        </Button>
      </div>

      {/* TEXTAREA */}
      <textarea
        ref={textareaRef}
        value={message}
        onChange={handleTextChange}
        onKeyPress={handleKeyPress}
        placeholder="Digite sua mensagem... (Ctrl+Enter para enviar)"
        style={{
          width: '100%',
          minHeight: '50px',
          maxHeight: '120px',
          fontFamily: GlassmorphismTheme.typography.fontFamily.primary,
          resize: 'none',
          ...GlassmorphismTheme.components.inputGlass,
        }}
      />

      {fileName && (
        <small style={{ display: 'block', marginTop: '8px', color: '#667eea' }}>
          <Icon name="check" /> {fileName} selecionado
        </small>
      )}
    </div>
  );
};

export default InputBox;
